import { Drawer, Spin } from "antd";
import { useForm } from "antd/es/form/Form";
import i18n from "i18next";
import { type FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { DataGroupSearchTerminalForm } from "src/app/features/DataGroup/components/forms/DataGroupSearchTerminalForm";
import type { TerminalList } from "src/app/features/Terminal/types/TerminalList";
import { createQueryParams, type QueryParamsConfig, useGetTableDataQuery } from "../../../../components";
import { SecondaryButton } from "../../../../components/Buttons/components/ActionButton";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from "../../../../components/ResourceManager/ResourceManager";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";

export const DataGroupSearchTerminalButton: FC = () => {
    const [open, setOpen] = useState(false);
    const [form] = useForm();

    const showDrawer = () => setOpen(true);
    const onClose = () => setOpen(false);

    const [query, setQuery] = useState<string>("");
    const [hasSearched, setHasSearched] = useState(false);
    const [showFooter, setShowFooter] = useState(false);

    const { data, isFetching } = useGetTableDataQuery(
        {
            resourceFetchPath: `data-groups/search-terminals`,
            query,
            dataMapperKey: "terminalTableData",
        },
        { skip: query === "" || !hasSearched },
    );

    const [queryConfig, setQueryConfig] = useState<QueryParamsConfig<TerminalList> | null>(null);

    useEffect(() => {
        if (!hasSearched || !queryConfig) return;
        const newQuery = createQueryParams(queryConfig);
        setQuery(newQuery);
    }, [queryConfig, hasSearched]);

    const onSearch = (filters: TerminalList) => {
        setHasSearched(true);
        setShowFooter(true);
        setQueryConfig({
            pageSize: DEFAULT_PAGE_SIZE,
            currentPage: DEFAULT_PAGE_NUMBER,
            filters,
            sorter: [],
        });
    };

    const onReset = () => {
        setShowFooter(false);
    };
    const navigate = useNavigate();

    const footerText = useMemo(() => {
        const list = data?.list ?? [];
        if (list.length > 0) {
            return (
                <div>
                    <p className="aevi-text">{`${list.length} terminal found:`}</p>
                    <div style={{ marginTop: 8 }}>
                        {list.map((item: TerminalList) => (
                            <>
                                <p className="aevi-text" key={item.id} style={{ marginBottom: 8 }}>
                                    <span className="aevi-text-bold">{i18n.t("list.searchTerminal.table.terminalId")}:</span>{" "}
                                    {item.terminalId}
                                    <br />
                                    <span className="aevi-text-bold">{i18n.t("list.searchTerminal.table.siteName")}:</span> {item.siteName}
                                    <br />
                                    <span className="aevi-text-bold">{i18n.t("list.searchTerminal.table.terminalProfile")}:</span>{" "}
                                    {item.terminalProfileName}
                                    <br />
                                    <span className="aevi-text-bold">{i18n.t("list.searchTerminal.table.status")}:</span>{" "}
                                    {item.status ? "ACTIVE" : "INACTIVE"}
                                    <br />
                                    <span className="aevi-text-bold">{i18n.t("list.searchTerminal.table.dataGroup")}:</span>{" "}
                                    {item.dataGroup}
                                </p>
                                <SecondaryButton
                                    onClick={() => navigate(`${BASE_PATH}/${ResourceName.TERMINALS}/${FORM_ACTION.EDIT}/${item.id}`)}
                                >
                                    {i18n.t("form.viewDetails")}
                                </SecondaryButton>
                            </>
                        ))}
                        <br />
                    </div>
                </div>
            );
        }
        return null;
    }, [data?.list, navigate]);

    return (
        <>
            <SecondaryButton onClick={showDrawer} disabled={false}>
                {i18n.t("list.searchTerminal")}
            </SecondaryButton>
            <Drawer
                title={i18n.t("list.searchTerminal.dialog.header.title")}
                closable={{ "aria-label": "Close Button" }}
                onClose={onClose}
                open={open}
            >
                <Spin spinning={isFetching}>
                    <DataGroupSearchTerminalForm form={form} searchCallback={onSearch} onReset={onReset} />
                    {showFooter && footerText && <div style={{ marginTop: 12 }}>{footerText}</div>}
                </Spin>
            </Drawer>
        </>
    );
};
